import * as React from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { Alert, Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DailyTotal, UpsellsResponse } from './types';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend);

// Upsell money per day over the page's period: what was charged, the
// contribution margin (collector economics.js, with the actual box freight of
// orders that have shipped: shippingActuals.js), running totals, and both per
// view. Each series can be switched on or off.

type Day = { date: string; viewed: number; accepted: number; revenue: number; margin: number | null; marginEstimate: number | null };

type SeriesKey =
  | 'revenue' | 'margin' | 'marginEstimate' | 'cumRevenue' | 'cumMargin' | 'revenuePerView' | 'marginPerView'
  | 'eligibleOfferedShare' | 'offeredShare' | 'viewedShare';
type Axis = 'daily' | 'cumulative' | 'perView' | 'share';

const SERIES: { key: SeriesKey; label: string; color: string; type: 'bar' | 'line'; axis: Axis; on: boolean }[] = [
  { key: 'revenue', label: 'Revenue', color: '#4F46E5', type: 'bar', axis: 'daily', on: true },
  { key: 'margin', label: 'Margin', color: '#10B981', type: 'bar', axis: 'daily', on: true },
  { key: 'marginEstimate', label: 'Margin, all estimated', color: '#A7F3D0', type: 'bar', axis: 'daily', on: false },
  { key: 'cumRevenue', label: 'Cumulative revenue', color: '#312E81', type: 'line', axis: 'cumulative', on: true },
  { key: 'cumMargin', label: 'Cumulative margin', color: '#047857', type: 'line', axis: 'cumulative', on: false },
  { key: 'revenuePerView', label: 'Revenue / view', color: '#F59E0B', type: 'line', axis: 'perView', on: false },
  { key: 'marginPerView', label: 'Margin / view', color: '#DC2626', type: 'line', axis: 'perView', on: false },
  { key: 'eligibleOfferedShare', label: '% of eligible orders offered', color: '#0369A1', type: 'line', axis: 'share', on: false },
  { key: 'offeredShare', label: '% of orders offered', color: '#0EA5E9', type: 'line', axis: 'share', on: false },
  { key: 'viewedShare', label: '% of orders saw it', color: '#8B5CF6', type: 'line', axis: 'share', on: false },
];

const AXIS_TITLES: Record<Axis, string> = { daily: 'Per day', cumulative: 'Cumulative', perView: 'Per view', share: '% of orders' };
const isShare = (key: SeriesKey) => SERIES.find(series => series.key === key)?.axis === 'share';
const percent = (share: number) => `${(share * 100).toFixed(share < 0.1 ? 1 : 0)}%`;

const dollars = (value: number) =>
  `${value < 0 ? '−' : ''}$${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: Math.abs(value) < 100 ? 2 : 0,
    maximumFractionDigits: Math.abs(value) < 100 ? 2 : 0,
  })}`;

// Every date from..to, so a day without an upsell reads as $0, not as missing.
function datesBetween(from: string, to: string) {
  const dates: string[] = [];
  for (let at = new Date(`${from}T12:00:00Z`); at <= new Date(`${to}T12:00:00Z`); at.setUTCDate(at.getUTCDate() + 1)) {
    dates.push(at.toISOString().slice(0, 10));
  }
  return dates;
}

const plus = (a: number | null, b: number | null) => (a == null || b == null ? null : a + b);

// Pure: the period's days, all upsells together. A day's margin is unknown if
// any of its upsells' is. An API without marginEstimate leaves it the margin.
export function dailyTotals(daily: DailyTotal[], from: string, to: string): Day[] {
  const empty = (date: string): Day => ({ date, viewed: 0, accepted: 0, revenue: 0, margin: 0, marginEstimate: 0 });
  const byDate = new Map<string, Day>();
  for (const row of daily) {
    const day = byDate.get(row.date) ?? empty(row.date);
    day.viewed += row.viewed;
    day.accepted += row.accepted;
    day.revenue += row.revenue;
    day.margin = plus(day.margin, row.margin);
    day.marginEstimate = plus(day.marginEstimate, row.marginEstimate === undefined ? row.margin : row.marginEstimate);
    byDate.set(row.date, day);
  }
  return datesBetween(from, to).map(date => byDate.get(date) ?? empty(date));
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
  );
}

export function RevenueChart({ data }: { data: UpsellsResponse }) {
  const [shown, setShown] = React.useState<Record<SeriesKey, boolean>>(
    () => Object.fromEntries(SERIES.map(series => [series.key, series.on])) as Record<SeriesKey, boolean>,
  );
  const days = React.useMemo(() => dailyTotals(data.daily, data.from, data.to), [data]);

  const total = days.reduce(
    (sum, day) => ({
      viewed: sum.viewed + day.viewed,
      accepted: sum.accepted + day.accepted,
      revenue: sum.revenue + day.revenue,
      margin: plus(sum.margin, day.margin),
      marginEstimate: plus(sum.marginEstimate, day.marginEstimate),
    }),
    { viewed: 0, accepted: 0, revenue: 0, margin: 0 as number | null, marginEstimate: 0 as number | null },
  );
  const perView = (value: number | null, views: number) => (value == null || !views ? null : value / views);

  // Orders (all, and those that could see the page), store revenue and the
  // checkouts offered / shown an upsell, per date (the warehouse's orders).
  const coverageByDate = React.useMemo(() => new Map((data.coverage ?? []).map(entry => [entry.date, entry])), [data]);
  // The period's totals count from its first day with an offer: days before the upsells went live (or were
  // switched on) had nothing to offer, and counting their orders read "Offered on 7%" over the last 30 days
  // when 39% was true since launch. With no offer in the period, the whole period counts.
  const coverage = [...(data.coverage ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  const firstOffer = coverage.findIndex(entry => entry.offered > 0);
  const live = firstOffer < 0 ? coverage : coverage.slice(firstOffer);
  const countedFrom = firstOffer > 0 ? live[0].date : null;
  const coverageTotal = live.reduce(
    (sum, entry) => ({
      orders: plus(sum.orders, entry.orders),
      // Missing from an API before eligibility was counted.
      eligibleOrders: plus(sum.eligibleOrders, entry.eligibleOrders ?? null),
      revenue: plus(sum.revenue, entry.revenue ?? null),
      offered: sum.offered + entry.offered,
      viewed: sum.viewed + entry.viewed,
    }),
    { orders: 0 as number | null, eligibleOrders: 0 as number | null, revenue: 0 as number | null, offered: 0, viewed: 0 },
  );

  const values: Record<SeriesKey, (number | null)[]> = React.useMemo(() => {
    const shareOf = (date: string, key: 'offered' | 'viewed', of: 'orders' | 'eligibleOrders' = 'orders') => {
      const entry = coverageByDate.get(date);
      const whole = entry?.[of];
      return entry && whole ? entry[key] / whole : null;
    };
    let cumRevenue = 0;
    let cumMargin: number | null = 0;
    const cumulative = days.map(day => {
      cumRevenue += day.revenue;
      cumMargin = cumMargin == null || day.margin == null ? null : cumMargin + day.margin;
      return { cumRevenue, cumMargin };
    });
    return {
      revenue: days.map(day => day.revenue),
      margin: days.map(day => day.margin),
      marginEstimate: days.map(day => day.marginEstimate),
      cumRevenue: cumulative.map(day => day.cumRevenue),
      cumMargin: cumulative.map(day => day.cumMargin),
      revenuePerView: days.map(day => perView(day.revenue, day.viewed)),
      marginPerView: days.map(day => perView(day.margin, day.viewed)),
      eligibleOfferedShare: days.map(day => shareOf(day.date, 'offered', 'eligibleOrders')),
      offeredShare: days.map(day => shareOf(day.date, 'offered')),
      viewedShare: days.map(day => shareOf(day.date, 'viewed')),
    };
  }, [days, coverageByDate]);

  const active = SERIES.filter(series => shown[series.key]);
  const axesInUse = new Set(active.map(series => series.axis));
  const chartData = {
    labels: days.map(day => day.date),
    datasets: active.map(series => ({
      type: series.type,
      label: series.label,
      data: values[series.key],
      yAxisID: series.axis,
      backgroundColor: series.color,
      borderColor: series.color,
      borderRadius: series.type === 'bar' ? 2 : undefined,
      borderWidth: series.type === 'line' ? 2 : 0,
      // A line through one day is a dot: draw the point, or it doesn't show.
      pointRadius: series.type === 'line' ? (days.length === 1 ? 4 : 0) : undefined,
      pointHoverRadius: 4,
      // Monotone: a smooth line that never swings past the points it joins.
      cubicInterpolationMode: 'monotone' as const,
      order: series.type === 'line' ? 0 : 1,
    })),
  };
  const scale = (axis: Axis, position: 'left' | 'right', grid: boolean) => ({
    display: axesInUse.has(axis),
    position,
    title: { display: true, text: AXIS_TITLES[axis], color: '#64748B' },
    grid: { display: grid },
    ticks: { callback: (value: number | string) => (axis === 'share' ? percent(Number(value)) : dollars(Number(value))) },
  });
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      // A Dashboard-page chart registers chartjs-plugin-datalabels globally.
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: any[]) =>
            new Date(`${items[0].label}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }),
          afterTitle: (items: any[]) => {
            const day = days[items[0].dataIndex];
            const orders = coverageByDate.get(day.date)?.orders;
            return `${orders != null ? `${orders} orders · ` : ''}${day.viewed} views · ${day.accepted} accepted`;
          },
          label: (item: any) =>
            ` ${item.dataset.label}: ${item.raw == null ? 'unknown' : SERIES.some(series => series.label === item.dataset.label && isShare(series.key)) ? percent(item.raw) : dollars(item.raw)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          autoSkipPadding: 12,
          callback: (_: unknown, index: number) =>
            new Date(`${days[index].date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
        },
      },
      daily: { ...scale('daily', 'left', true), beginAtZero: true },
      cumulative: { ...scale('cumulative', 'right', false), beginAtZero: true },
      perView: scale('perView', 'right', false),
      share: { ...scale('share', 'right', false), beginAtZero: true, suggestedMax: 1 },
    },
  };

  const range = data.days ? `last ${data.days} days` : data.from === data.to ? data.from : `${data.from} to ${data.to}`;
  const shippedShare = data.economics.marginShippedShare;
  const excluded = coverageTotal.orders != null && coverageTotal.eligibleOrders != null ? coverageTotal.orders - coverageTotal.eligibleOrders : null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-label="upsell revenue chart">
      <Typography variant="subtitle1" fontWeight={600}>Upsell money by day</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        All upsells, {range} (Pacific). Revenue is the extra charged, before tax. Margin is revenue less the
        extra product cost (Shopify COGS), the card fee on the charge and the extra freight. For orders that have shipped
        the freight is actual: what ShipBob billed for the order's boxes less the median box of orders with the same items
        without the upsell (the Shipping & margin tab). Until an order ships it is estimated: for add-ons about{' '}
        {data.economics.freightPerUnit == null ? 'one more item' : dollars(data.economics.freightPerUnit)} per item
        added; before refunds.
        {Object.entries(data.economics.swapFreight ?? {}).map(([upsellId, freight]) => (
          <React.Fragment key={upsellId}>
            {' '}
            {upsellId}: the replaced items no longer ship, so their cost is netted out;{' '}
            {freight.perSwap == null
              ? `its box's extra freight isn't counted yet (${freight.sample} invoiced, needs 5).`
              : `its box ships for ${dollars(Math.abs(freight.perSwap))} ${freight.perSwap >= 0 ? 'more' : 'less'} (${freight.sample} invoiced).`}
          </React.Fragment>
        ))}
      </Typography>
      {data.economics.problem && <Alert severity="warning" sx={{ mb: 1.5 }}>{data.economics.problem}</Alert>}
      {data.economics.shippingProblem && <Alert severity="warning" sx={{ mb: 1.5 }}>{data.economics.shippingProblem}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
        <Figure label="Revenue" value={dollars(total.revenue)} hint={`${total.accepted} accepted`} />
        <Figure
          label="Margin"
          value={total.margin == null || data.economics.problem ? 'unknown' : dollars(total.margin)}
          hint={
            shippedShare == null
              ? 'estimated'
              : `${percent(shippedShare)} on actual shipping${total.marginEstimate != null && total.marginEstimate !== total.margin ? ` · all estimated: ${dollars(total.marginEstimate)}` : ''}`
          }
        />
        <Figure
          label="Share of store revenue"
          value={coverageTotal.revenue ? percent(total.revenue / coverageTotal.revenue) : '—'}
          hint={coverageTotal.revenue ? `upsells ${dollars(total.revenue)} of ${dollars(coverageTotal.revenue)} (subtotals, before tax)${countedFrom ? `, since ${countedFrom}` : ''}` : coverageTotal.revenue == null ? 'no store revenue for this store' : 'no orders yet'}
        />
        <Figure label="Revenue / view" value={total.viewed ? dollars(total.revenue / total.viewed) : '—'} hint={`${total.viewed} views`} />
        <Figure label="Margin / view" value={total.viewed && total.margin != null ? dollars(total.margin / total.viewed) : '—'} />
        {coverageTotal.eligibleOrders ? (
          <Tooltip
            title={
              'Orders that could see it: not paid with Apple Pay, Google Pay, Shop Pay Installments, Affirm, Afterpay, Klarna or ' +
              'Shop Cash (Shopify never shows the post-purchase page to those), and not $0.'
            }
          >
            <Box>
              <Figure
                label="Offered on"
                value={`${percent(coverageTotal.offered / coverageTotal.eligibleOrders)} of orders that could see it`}
                hint={`${coverageTotal.offered} of ${coverageTotal.eligibleOrders} · of all orders: ${coverageTotal.orders ? percent(coverageTotal.offered / coverageTotal.orders) : '—'} · ${excluded} excluded${countedFrom ? ` · since ${countedFrom}, the period's first day with an offer` : ''}`}
              />
            </Box>
          </Tooltip>
        ) : (
          <Figure
            label="Offered on"
            value={coverageTotal.orders ? `${percent(coverageTotal.offered / coverageTotal.orders)} of orders` : '—'}
            hint={
              coverageTotal.orders
                ? `${coverageTotal.offered} of ${coverageTotal.orders} · saw it: ${percent(coverageTotal.viewed / coverageTotal.orders)}`
                : coverageTotal.orders == null
                  ? 'no order count for this store'
                  : 'no orders yet'
            }
          />
        )}
      </Box>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }} aria-label="series">
        {SERIES.map(series => (
          <Chip
            key={series.key}
            size="small"
            label={series.label}
            onClick={() => setShown(current => ({ ...current, [series.key]: !current[series.key] }))}
            variant={shown[series.key] ? 'filled' : 'outlined'}
            sx={
              shown[series.key]
                ? { bgcolor: series.color, color: '#fff', '&:hover': { bgcolor: series.color, opacity: 0.85 } }
                : { borderColor: series.color, color: series.color }
            }
            aria-pressed={shown[series.key]}
          />
        ))}
      </Stack>
      <Box sx={{ height: 300 }}>
        {active.length ? (
          <Chart type="bar" data={chartData as any} options={options as any} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 4, textAlign: 'center' }}>
            Pick a series above to plot it.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
